import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const DischargeSummaryForm = ({
    formData,
    handleChange,
    handleMedicationsChange,
    program,
    isSubmitting = false,
    isEdit = false,
    fileData = {} // Add fileData prop
}) => {
    const [medications, setMedications] = useState(
        formData.discharge_medications || [
            { id: Date.now(), name: "", dosage: "", frequency: "" },
        ]
    );

    // --- CNIC Masking Logic ---
    const formatCNIC = (value) => {
        if (!value) return value;
        const cnic = value.replace(/[^\d]/g, ""); // Remove non-digits
        const cnicLen = cnic.length;

        if (cnicLen <= 5) return cnic;
        if (cnicLen <= 12) return `${cnic.slice(0, 5)}-${cnic.slice(5)}`;
        return `${cnic.slice(0, 5)}-${cnic.slice(5, 12)}-${cnic.slice(12, 13)}`;
    };

    const handleCNICChange = (e) => {
        const maskedValue = formatCNIC(e.target.value);
        // Create a synthetic event to stay compatible with your parent handleChange
        const syntheticEvent = {
            target: {
                name: e.target.name,
                value: maskedValue
            }
        };
        handleChange(syntheticEvent);
    };

    // Use ref to track if this is the initial render
    const initialRender = useRef(true);
    const prevMedicationsRef = useRef(medications);

    // Sync medications with parent ONLY when medications actually change
    useEffect(() => {
        // Skip the initial render
        if (initialRender.current) {
            initialRender.current = false;
            return;
        }

        // Only call handleMedicationsChange if medications actually changed
        const medicationsChanged = JSON.stringify(medications) !== JSON.stringify(prevMedicationsRef.current);

        if (medicationsChanged && handleMedicationsChange) {
            handleMedicationsChange(medications);
            prevMedicationsRef.current = medications;
        }
    }, [medications, handleMedicationsChange]);

    // Initialize prevMedicationsRef on first render
    useEffect(() => {
        prevMedicationsRef.current = medications;
    }, []);

    useEffect(() => {
        if (formData?.discharge_medications?.length) {
            // Normalize incoming meds so each has an `id`
            const normalized = formData.discharge_medications.map((m, idx) => ({
                ...m,
                id: m.id || m.tempId || Date.now() + idx,
            }));
            setMedications(normalized);
            prevMedicationsRef.current = normalized;
        }
    }, [formData.discharge_medications]);

    // ➕ Add medication row
    // const addMedication = () => {
    //     const newMedications = [
    //         ...medications,
    //         { id: Date.now(), name: "", dosage: "", frequency: "" },
    //     ];
    //     setMedications(newMedications);
    // };

    const addMedication = () => {
        const newMedications = [
            ...medications,
            { id: Date.now(), name: "", dosage: "", frequency: "", route: "" },
        ];
        setMedications(newMedications);
    };

    // ❌ Remove medication row
    const removeMedication = (id) => {
        if (medications.length === 1) return;
        const newMedications = medications.filter((m) => m.id !== id && m.tempId !== id);
        setMedications(newMedications);
    };

    // 🖊 Handle medication change
    const handleMedChange = (id, field, value) => {
        const updated = medications.map((m) =>
            (m.id === id || m.tempId === id) ? { ...m, [field]: value } : m
        );
        setMedications(updated);
    };

    // Helper function to get file name from path
    const getFileNameFromPath = (path) => {
        if (!path) return null;
        return path.split('/').pop();
    };

    // Helper function to check if file is an image
    const isImageFile = (fileName) => {
        if (!fileName) return false;
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
        return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
    };

    return (
        <div className="p-4">
            <div className="accordion" id="dischargeSummaryAccordion">

                {/* ================================================== */}
                {/* === BASIC INFORMATION ============================ */}
                {/* ================================================== */}
                <div className="accordion-item mb-2">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#basicInfo"
                        >
                            Basic Information
                        </button>
                    </h2>
                    <div id="basicInfo" className="accordion-collapse collapse show">
                        <div className="accordion-body mt-2">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            name="client_name"
                                            className="form-control"
                                            placeholder="Client Name"
                                            value={formData.client_name || ""}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                            readOnly={!!formData.patient_id}
                                        />
                                        <label>Client Name</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            name="CNIC"
                                            className="form-control"
                                            placeholder="12345-6789101-2"
                                            maxLength="15" // 13 digits + 2 dashes
                                            value={formData.CNIC || ""}
                                            onChange={handleCNICChange}
                                            disabled={isSubmitting}
                                            readOnly={!!formData.patient_id}
                                        />
                                        <label>CNIC</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="gender"
                                            value={formData.gender?.toLowerCase() || ""}
                                            onChange={handleChange}
                                            disabled={isSubmitting || !!formData.patient_id}
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                        <label>Gender</label>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-floating form-floating-outline">
                                        <select
                                            className="form-select"
                                            name="program"
                                            value={formData.program || ""}
                                            onChange={handleChange}
                                            disabled
                                        >
                                            <option value="">Select Program</option>
                                            {formData.program && !["Day Rehabilitation Program", "Residential Rehab Program", "Home Visitation Program"].includes(formData.program) && (
                                                <option value={formData.program}>{formData.program}</option>
                                            )}
                                            <option value="Day Rehabilitation Program">Day Rehabilitation Program</option>
                                            <option value="Residential Rehab Program">Residential Rehab Program</option>
                                            <option value="Home Visitation Program">Home Visitation Program</option>
                                        </select>
                                        <label>Program</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="date"
                                            name="admission_date"
                                            className="form-control"
                                            value={formData.admission_date || ""}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                        />
                                        <label>Date of Admission</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="date"
                                            name="discharge_date"
                                            className="form-control"
                                            value={formData.discharge_date || ""}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                        />
                                        <label>Date of Discharge</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            name="discharge_reason"
                                            className="form-control"
                                            placeholder="Reason for Discharge"
                                            value={formData.discharge_reason || ""}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                        />
                                        <label>Reason for Discharge</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================== */}
                {/* === Provisional Psychiatric Diagnosis ========================== */}
                {/* ================================================== */}
                <div className="accordion-item mb-2">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#provisionalPsychiatricDiagnosis"
                        >
                            Provisional Psychiatric Diagnosis
                        </button>
                    </h2>

                    <div id="provisionalPsychiatricDiagnosis" className="accordion-collapse collapse">
                        <div className="accordion-body mt-2">
                            <div className="row g-3">

                                {/* Primary Diagnosis */}
                                <div className="col-md-12">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="primary_diagnosis"
                                            placeholder="Primary Diagnosis"
                                            value={formData.primary_diagnosis || ""}
                                            onChange={handleChange}
                                            style={{ minHeight: "90px" }}
                                            disabled={isSubmitting}
                                        />
                                        <label>Primary Diagnosis</label>
                                    </div>
                                </div>

                                {/* Secondary / Co-morbidities */}
                                <div className="col-md-12">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="secondary_diagnosis"
                                            placeholder="Secondary / Co-morbidities"
                                            value={formData.secondary_diagnosis || ""}
                                            onChange={handleChange}
                                            style={{ minHeight: "90px" }}
                                            disabled={isSubmitting}
                                        />
                                        <label>Secondary / Co-morbidities</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================== */}
                {/* === Discharge Planning ============================ */}
                {/* ================================================== */}
                <div className="accordion-item mb-2">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#dischargePlanning"
                        >
                            Discharge Planning
                        </button>
                    </h2>

                    <div id="dischargePlanning" className="accordion-collapse collapse">
                        <div className="accordion-body mt-2">

                            {/* SECTION TITLE */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                                Discharge Type
                            </small>

                            {/* DISCHARGE TYPE CHECKBOXES */}
                            <div className="row g-2">
                                {[
                                    "Planned",
                                    "Unplanned",
                                    "Rehab goals achieved to desired level",
                                    "Resistent to rehab intervention",
                                    "Other"
                                ].map((option) => {
                                    const checked = formData.discharge_types?.includes(option);
                                    const inputId = `dischargeType-${option.replace(/\s+/g, "-")}`;

                                    return (
                                        <div className="col-md-4 col-sm-6" key={option}>
                                            <div className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={inputId}
                                                    name="discharge_types"
                                                    value={option}
                                                    checked={checked}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label" htmlFor={inputId}>
                                                    {option}
                                                </label>
                                            </div>

                                            {/* Conditional Textbox for OTHER */}
                                            {option === "Other" && checked && (
                                                <div className="form-floating form-floating-outline mb-2">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="discharge_type_other_value"
                                                        value={formData.discharge_type_other_value || ""}
                                                        onChange={handleChange}
                                                        placeholder="Specify"
                                                    />
                                                    <label>Specify</label>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* ================================================== */}
                            {/* Other Checkbox Options (Simple) */}
                            {/* ================================================== */}

                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1 mt-4">
                                Additional Discharge Options
                            </small>

                            <div className="row g-2">
                                {[
                                    "Transferred to other facility (Referral form provided)",
                                    "LAMA (Counseling done & consequences explained)",
                                    "DOR – Discharge On Request"
                                ].map((option) => {
                                    const checked = formData.discharge_extras?.includes(option);
                                    const inputId = `dischargeExtra-${option.replace(/\s+/g, "-")}`;

                                    return (
                                        <div className="col-md-4 col-sm-6" key={option}>
                                            <div className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={inputId}
                                                    name="discharge_extras"
                                                    value={option}
                                                    checked={checked}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label" htmlFor={inputId}>
                                                    {option}
                                                </label>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* ================================================== */}
                            {/* Multi-line Textarea Fields */}
                            {/* ================================================== */}
                            <div className="row mt-4 g-3">
                                {[
                                    { label: "Discharge Note", name: "discharge_note" },
                                    { label: "Plan for Follow-up", name: "follow_up_plan" },
                                    { label: "Special Instructions", name: "special_instructions" },
                                    { label: "Injections if Any", name: "injections" },
                                    { label: "Red Flags", name: "red_flags" },
                                    { label: "Risk Assessment at Discharge", name: "risk_assessment" }
                                ].map((field) => (
                                    <div className="col-md-12" key={field.name}>
                                        <div className="form-floating form-floating-outline">
                                            <textarea
                                                className="form-control"
                                                name={field.name}
                                                placeholder={field.label}
                                                value={formData[field.name] || ""}
                                                onChange={handleChange}
                                                style={{ minHeight: "90px" }}
                                            />
                                            <label>{field.label}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ================================================== */}
                            {/* Teaching Provided To (Checkboxes Matching UI) */}
                            {/* ================================================== */}
                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1 mt-4">
                                Teaching Provided To
                            </small>

                            <div className="row g-2">
                                {["Client", "Family", "Guardian"].map((option) => {
                                    const checked = formData.teaching_provided_to?.includes(option);
                                    const inputId = `teachingTo-${option}`;

                                    return (
                                        <div className="col-md-4 col-sm-6" key={option}>
                                            <div className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={inputId}
                                                    name="teaching_provided_to"
                                                    value={option}
                                                    checked={checked}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label" htmlFor={inputId}>
                                                    {option}
                                                </label>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Presenting Symptoms Accordion */}
                <div className="accordion-item mb-2">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#presentingSymptomsAccordion"
                            aria-expanded="false"
                            aria-controls="presentingSymptomsAccordion"
                        >
                            Presenting Symptoms
                        </button>
                    </h2>

                    <div
                        id="presentingSymptomsAccordion"
                        className="accordion-collapse collapse"
                        aria-labelledby="presentingSymptomsAccordion"
                    >
                        <div className="accordion-body mt-2">
                            <div className="form-floating form-floating-outline">
                                <textarea
                                    className="form-control"
                                    name="presenting_symptoms"
                                    placeholder="Presenting Symptoms"
                                    value={formData.presenting_symptoms || ""}
                                    onChange={handleChange}
                                    style={{ minHeight: "90px" }}
                                    disabled={isSubmitting}
                                />
                                <label>Presenting Symptoms</label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================== */}
                {/* === SERVICES PROVIDED ============================ */}
                {/* ================================================== */}
                <div className="accordion-item mb-2">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#servicesProvided"
                            aria-expanded="false"
                            aria-controls="servicesProvided"
                        >
                            Services Provided at TRH
                        </button>
                    </h2>

                    <div
                        id="servicesProvided"
                        className="accordion-collapse collapse"
                        aria-labelledby="servicesProvided"
                    >
                        <div className="accordion-body mt-2">
                            <div className="row">
                                {[
                                    "Psychiatric consultation & treatment",
                                    "Primary-care services",
                                    "Psychiatric rehabilitation services",
                                    "Individual Therapy",
                                    "Group Therapy",
                                    "Psychoeducation",
                                    "Family Counseling",
                                    "Recreational / Occupational Therapy"
                                ].map((service, idx) => (
                                    <div className="col-md-6" key={idx}>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="service_provided" // same as in handleChange
                                                value={service}
                                                checked={formData.service_provided?.includes(service)}
                                                onChange={handleChange} // use parent handler
                                                id={`service-${idx}`}
                                                disabled={isSubmitting}
                                            />
                                            <label className="form-check-label" htmlFor={`service-${idx}`}>
                                                {service}
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================== */}
                {/* === REPORT SECTIONS =============================== */}
                {/* ================================================== */}
                {[
                    { title: "Primary Care Physician's Report", key: "primary_care" },
                    { title: "Psychiatrist's Report", key: "psychiatrist" },
                    { title: "Care Manager's Report", key: "care_manager" },
                ].map((section) => (
                    <div className="accordion-item mb-2" key={section.key}>
                        <h2 className="accordion-header">
                            <button
                                className="accordion-button collapsed"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#${section.key}`}
                            >
                                {section.title}
                            </button>
                        </h2>
                        <div
                            id={section.key}
                            className="accordion-collapse collapse"
                        >
                            <div className="accordion-body mt-2">
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <input
                                            type="file"
                                            name={`${section.key}_attachment`}
                                            className="form-control"
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        />

                                        {/* Show existing file info in edit mode */}
                                        {isEdit && fileData[`${section.key}_attachment`]?.existingUrl && (
                                            <div className="mt-3 p-3 border rounded bg-light">
                                                <h6 className="mb-2">
                                                    <i className="ri-file-line me-2"></i>
                                                    Current File:
                                                </h6>
                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                    <span className="badge bg-success">
                                                        {getFileNameFromPath(fileData[`${section.key}_attachment`].existingPath)}
                                                    </span>
                                                </div>

                                                {/* Preview for images */}
                                                {isImageFile(fileData[`${section.key}_attachment`].existingPath) && (
                                                    <div className="mb-2">
                                                        <img
                                                            src={fileData[`${section.key}_attachment`].existingUrl}
                                                            alt="Preview"
                                                            className="img-thumbnail"
                                                            style={{ maxHeight: '150px', maxWidth: '100%' }}
                                                        />
                                                    </div>
                                                )}

                                                <div className="d-flex gap-2">
                                                    <a
                                                        href={fileData[`${section.key}_attachment`].existingUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-primary"
                                                    >
                                                        <i className="ri-eye-line me-1"></i> View
                                                    </a>
                                                    <a
                                                        href={fileData[`${section.key}_attachment`].existingUrl}
                                                        download
                                                        className="btn btn-sm btn-outline-success"
                                                    >
                                                        <i className="ri-download-line me-1"></i> Download
                                                    </a>
                                                </div>
                                            </div>
                                        )}

                                        <small className="text-muted d-block mt-1">
                                            PDF, Word, JPEG, PNG (Max 10MB)
                                        </small>
                                    </div>
                                    <div className="col-md-8">
                                        <div className="form-floating form-floating-outline">
                                            <textarea
                                                className="form-control"
                                                name={`${section.key}_remarks`}
                                                placeholder="Remarks"
                                                rows={3}
                                                value={formData[`${section.key}_remarks`] || ""}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                            <label>Remarks</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {/* ================================================== */}
                {/* === CLINICAL PROGRESS & MENTAL STATE ========= */}
                {/* ================================================== */}
                <div className="accordion-item mb-2">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#clinicalProgress"
                        >
                            Clinical Progress & Mental State
                        </button>
                    </h2>

                    <div id="clinicalProgress" className="accordion-collapse collapse">
                        <div className="accordion-body">
                            {/* Progress */}
                            <div className="col-md-12 mb-3">
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-0">
                                    Progress:
                                </small>
                                <div className="row">
                                    {["Marked Improvement", "Moderate Progress", "No Change", "Needs Further Intervention"].map((item, idx) => (
                                        <div className="col-md-3" key={idx}>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="clinical_progress"
                                                    value={item}
                                                    checked={formData.clinical_progress?.includes(item)}
                                                    onChange={handleChange}
                                                    id={`progress-${idx}`}
                                                    disabled={isSubmitting}
                                                />
                                                <label className="form-check-label" htmlFor={`progress-${idx}`}>
                                                    {item}
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Mental State at Discharge */}
                            <div className="col-md-12 mb-3">
                                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-0">
                                    Mental State at Discharge:
                                </small>
                                <div className="row">
                                    {[
                                        "Stable",
                                        "Improved",
                                        "Readiness to Discharge",
                                        "Risk Managed",
                                        "Minimal Supervision Required",
                                        "Requires Long-Term Monitoring",
                                        "Insight Improved but Partial",
                                        "Symptoms Persist – Controlled",
                                        "Behavioral Support Needed Complications"
                                    ].map((item, idx) => (
                                        <div className="col-md-3 mb-2" key={idx}>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="mental_state_at_discharge"
                                                    value={item}
                                                    checked={formData.mental_state_at_discharge?.includes(item)}
                                                    onChange={handleChange}
                                                    id={`mental-state-${idx}`}
                                                    disabled={isSubmitting}
                                                />
                                                <label className="form-check-label" htmlFor={`mental-state-${idx}`}>
                                                    {item}
                                                </label>

                                                {/* Show text field only if "Behavioral Support Needed Complications" is checked */}
                                                {item === "Behavioral Support Needed Complications" &&
                                                    formData.mental_state_at_discharge?.includes(item) && (
                                                        <input
                                                            type="text"
                                                            className="form-control mt-1"
                                                            name="complications"
                                                            value={formData.complications || ""}
                                                            onChange={handleChange}
                                                            disabled={isSubmitting}
                                                            placeholder="Enter complications"
                                                        />
                                                    )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================== */}
                {/* === DISCHARGE MEDICATIONS ======================== */}
                {/* ================================================== */}
                <div className="accordion-item mb-2">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#dischargeMedications"
                        >
                            Discharge Medications
                        </button>
                    </h2>
                    <div
                        id="dischargeMedications"
                        className="accordion-collapse collapse"
                    >
                        <div className="accordion-body mt-2">
                            {medications.map((row, idx) => (
                                <div
                                    key={row.id || row.tempId}
                                    className="border rounded-4 p-3 mb-3 bg-light-subtle"
                                >
                                    <div className="row g-3 align-items-center">
                                        {/* Medication Name */}
                                        <div className="col-md-4">
                                            <div className="form-floating form-floating-outline">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Medication Name"
                                                    value={row.name}
                                                    onChange={(e) =>
                                                        handleMedChange(row.id || row.tempId, "name", e.target.value)
                                                    }
                                                    disabled={isSubmitting}
                                                />
                                                <label>Medication Name</label>
                                            </div>
                                        </div>

                                        {/* Dosage */}
                                        <div className="col-md-2">
                                            <div className="form-floating form-floating-outline">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Dosage"
                                                    value={row.dosage}
                                                    onChange={(e) =>
                                                        handleMedChange(row.id || row.tempId, "dosage", e.target.value)
                                                    }
                                                    disabled={isSubmitting}
                                                />
                                                <label>Dosage</label>
                                            </div>
                                        </div>

                                        {/* Frequency */}
                                        <div className="col-md-2">
                                            <div className="form-floating form-floating-outline">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Frequency"
                                                    value={row.frequency}
                                                    onChange={(e) =>
                                                        handleMedChange(row.id || row.tempId, "frequency", e.target.value)
                                                    }
                                                    disabled={isSubmitting}
                                                />
                                                <label>Frequency</label>
                                            </div>
                                        </div>

                                        {/* Route (Radio Buttons) */}
                                        <div className="col-md-3">
                                            <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-0">
                                                Route
                                            </small>
                                            <div className="d-flex gap-2">
                                                {["Oral", "IM", "IV"].map((option) => (
                                                    <div className="form-check form-check-inline" key={option}>
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name={`route-${row.id || row.tempId}`}
                                                            value={option}
                                                            checked={row.route === option}
                                                            onChange={(e) =>
                                                                handleMedChange(row.id || row.tempId, "route", e.target.value)
                                                            }
                                                            disabled={isSubmitting}
                                                            id={`route-${row.id || row.tempId}-${option}`}
                                                        />
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor={`route-${row.id || row.tempId}-${option}`}
                                                        >
                                                            {option}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Delete Button */}
                                        <div className="col-md-1 text-center">
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger"
                                                onClick={() => removeMedication(row.id || row.tempId)}
                                                disabled={isSubmitting || medications.length === 1}
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Add Row */}
                            <div className="text-end mt-3">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={addMedication}
                                    disabled={isSubmitting}
                                >
                                    <i className="ri-add-line me-1"></i> Add Row
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================== */}
                {/* === INSTRUCTIONS ================================ */}
                {/* ================================================== */}
                <div className="accordion-item mb-2">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#instructions"
                        >
                            Instructions
                        </button>
                    </h2>

                    <div id="instructions" className="accordion-collapse collapse">
                        <div className="accordion-body">
                            <div className="col-md-12 mb-3">
                                <div className="row">
                                    {[
                                        "Verbal instructions given",
                                        "Written prescription attached",
                                        "Continue till next psychiatric review",
                                        "Side effects & compliance discussed"
                                    ].map((item, idx) => (
                                        <div className="col-md-6" key={idx}>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="instructions"
                                                    value={item}
                                                    checked={formData.instructions?.includes(item)}
                                                    onChange={handleChange}
                                                    id={`instruction-${idx}`}
                                                    disabled={isSubmitting}
                                                />
                                                <label className="form-check-label" htmlFor={`instruction-${idx}`}>
                                                    {item}
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================== */}
                {/* === FAMILY COUNSELING CHECKLIST ================= */}
                {/* ================================================== */}
                <div className="accordion-item mb-2">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#familyCounseling"
                        >
                            Family Counseling Checklist
                        </button>
                    </h2>

                    <div id="familyCounseling" className="accordion-collapse collapse">
                        <div className="accordion-body">
                            <div className="col-md-12 mb-3">
                                <label className="form-label fw-bold">Family Counseling:</label>
                                <div className="row">
                                    {[
                                        "Educated about illness condition",
                                        "Educated on hygiene care at home",
                                        "Educated on medication use & side effects",
                                        "Medicine to be kept with family & supervised",
                                        "Monitor unusual behaviors & report in OPD",
                                        "After giving medicine -> check patient’s mouth -> supervise for 20 mins",
                                        "Informed about follow-up after 14 days / earlier if symptoms worsen",
                                        "Educated on adjusting family expectations"
                                    ].map((item, idx) => (
                                        <div className="col-md-6" key={idx}>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="family_counseling"
                                                    value={item}
                                                    checked={formData.family_counseling?.includes(item)}
                                                    onChange={handleChange}
                                                    id={`family-counseling-${idx}`}
                                                    disabled={isSubmitting}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor={`family-counseling-${idx}`}
                                                >
                                                    {item}
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================== */}
                {/* === HOME CARE & FUTURE SUPPORT ================== */}
                {/* ================================================== */}
                <div className="accordion-item mb-2">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#homeCareSupport"
                        >
                            Home Care & Future Support
                        </button>
                    </h2>

                    <div id="homeCareSupport" className="accordion-collapse collapse">
                        <div className="accordion-body">
                            <div className="row g-3 mt-2">
                                {[
                                    { label: "Possible Relapse Signs", name: "possible_relapse_signs" },
                                    { label: "Recovery Goals", name: "recovery_goals" },
                                    { label: "Who to Contact", name: "who_to_contact" },
                                    { label: "Where to Go in Crisis", name: "where_to_go_crisis" },
                                    { label: "Medication Details", name: "medication_details" },
                                    { label: "Comprehensive Treatment Plan", name: "treatment_plan" },
                                    { label: "Physical Health Needs", name: "physical_health_needs" },
                                    { label: "Date of Review", name: "date_of_review", type: "date" },
                                    { label: "Admission Pathway", name: "admission_pathway" }
                                ].map((field, idx) => (
                                    <div className="col-md-4" key={idx}>
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type={field.type || "text"}
                                                className="form-control"
                                                placeholder={field.label}
                                                name={field.name}
                                                value={formData[field.name] || ""}
                                                onChange={handleChange}
                                                disabled={isSubmitting}
                                            />
                                            <label>{field.label}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================== */}
                {/* === FOLLOW-UP & REFERRAL ======================= */}
                {/* ================================================== */}
                <div className="accordion-item mb-2">
                    <h2 className="accordion-header">
                        <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#followUpReferral"
                        >
                            Follow-up & Referral
                        </button>
                    </h2>

                    <div id="followUpReferral" className="accordion-collapse collapse">
                        <div className="accordion-body">
                            <div className="row g-3 mt-2">

                                {/* ======================= */}
                                {/* ROW 1 — 3 COLUMNS      */}
                                {/* ======================= */}

                                {/* Follow-up Psychiatrist Appointment */}
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="follow_up_psychiatrist"
                                            value={formData.follow_up_psychiatrist || ""}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                        />
                                        <label>Follow-up Psychiatrist Appointment</label>
                                    </div>
                                </div>

                                {/* Referred To */}
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="referred_to_other_text"
                                            value={formData.referred_to_other_text || ""}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                            placeholder="Specify referral"
                                        />
                                        <label>Referred To (Specify)</label>
                                    </div>
                                </div>

                                {/* Referred For */}
                                <div className="col-md-12">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-0">
                                        Referred For:
                                    </small>

                                    <div className="row">

                                        {/* Day Care */}
                                        <div className="col-md-4">
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    name="referred_for"
                                                    value="Day Care"
                                                    checked={formData.referred_for.includes("Day Care")}
                                                    onChange={handleChange}
                                                    id="refForDaycare"
                                                    disabled={isSubmitting}
                                                />
                                                <label className="form-check-label" htmlFor="refForDaycare">
                                                    Day Care
                                                </label>
                                            </div>
                                        </div>

                                        {/* Home Visiting */}
                                        <div className="col-md-4">
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    name="referred_for"
                                                    value="Home Visiting"
                                                    checked={formData.referred_for.includes("Home Visiting")}
                                                    onChange={handleChange}
                                                    id="refForHome"
                                                    disabled={isSubmitting}
                                                />
                                                <label className="form-check-label" htmlFor="refForHome">
                                                    Home Visiting
                                                </label>
                                            </div>
                                        </div>

                                        {/* Other */}
                                        <div className="col-md-4">
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    name="referred_for"
                                                    value="Other"
                                                    checked={formData.referred_for.includes("Other")}
                                                    onChange={handleChange}
                                                    id="refForOther"
                                                    disabled={isSubmitting}
                                                />
                                                <label className="form-check-label" htmlFor="refForOther">
                                                    Other
                                                </label>
                                            </div>

                                            {/* Show text field if "Other" is selected */}
                                            {formData.referred_for.includes("Other") && (
                                                <input
                                                    type="text"
                                                    className="form-control mt-1"
                                                    name="referred_for_other_text"
                                                    value={formData.referred_for_other_text || ""}
                                                    onChange={handleChange}
                                                    disabled={isSubmitting}
                                                    placeholder="Specify other referral"
                                                />
                                            )}
                                        </div>

                                    </div>
                                </div>


                                {/* ======================= */}
                                {/* ROW 2 — 2 COLUMNS      */}
                                {/* ======================= */}

                                {/* Next of Kin Informed */}
                                {/* Next of Kin Informed */}
                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-0 text-center">
                                        Next of Kin Informed:
                                    </small>

                                    <div className="d-flex justify-content-center align-items-center gap-4 mt-1">
                                        {["Yes", "No"].map((option) => (
                                            <div className="form-check m-0" key={option}>
                                                <input
                                                    type="radio"
                                                    className="form-check-input"
                                                    name="next_of_kin_informed"
                                                    value={option}
                                                    checked={formData.next_of_kin_informed === option}
                                                    onChange={handleChange}
                                                    id={`nextOfKin-${option}`}
                                                    disabled={isSubmitting}
                                                />
                                                <label className="form-check-label" htmlFor={`nextOfKin-${option}`}>
                                                    {option}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Emergency Contact Provided */}
                                <div className="col-md-6">
                                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-0 text-center">
                                        Emergency Contact Provided:
                                    </small>

                                    <div className="d-flex justify-content-center align-items-center gap-4 mt-1">
                                        {["Yes", "No"].map((option) => (
                                            <div className="form-check m-0" key={option}>
                                                <input
                                                    type="radio"
                                                    className="form-check-input"
                                                    name="emergency_contact_provided"
                                                    value={option}
                                                    checked={formData.emergency_contact_provided === option}
                                                    onChange={handleChange}
                                                    id={`emergencyContact-${option}`}
                                                    disabled={isSubmitting}
                                                />
                                                <label className="form-check-label" htmlFor={`emergencyContact-${option}`}>
                                                    {option}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* ======================= */}
                                {/* ROW 3 — FULL WIDTH     */}
                                {/* ======================= */}

                                {/* Safety Care Plan */}
                                <div className="col-md-12 mt-3">
                                    <div className="form-floating form-floating-outline">
                                        <textarea
                                            className="form-control"
                                            name="safety_care_plan"
                                            value={formData.safety_care_plan || ""}
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                            style={{ minHeight: "90px" }}
                                            placeholder="Safety Care Plan"
                                        />
                                        <label>Safety Care Plan (if needed)</label>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                {/* ================================================== */}
                {/* === FINAL SECTIONS =============================== */}
                {/* ================================================== */}
                {[
                    {
                        title: "Recommendations from TRH Team",
                        name: "trh_recommendations",
                    },
                ].map((field) => (
                    <div className="accordion-item mb-2" key={field.name}>
                        <h2 className="accordion-header">
                            <button
                                className="accordion-button collapsed"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#${field.name}`}
                            >
                                {field.title}
                            </button>
                        </h2>
                        <div
                            id={field.name}
                            className="accordion-collapse collapse"
                        >
                            <div className="accordion-body mt-2">
                                <div className="form-floating form-floating-outline">
                                    <textarea
                                        className="form-control"
                                        name={field.name}
                                        placeholder={field.title}
                                        value={formData[field.name] || ""}
                                        onChange={handleChange}
                                        style={{ minHeight: "100px" }}
                                        disabled={isSubmitting}
                                    />
                                    <label>{field.title}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DischargeSummaryForm;